/**
 * Shared game-settings panel used by every lobby surface (PvPSetup's
 * Online / Bot / BotVsBot setups, including the deck-builder draft mode
 * via the `draft_enabled` toggle).
 *
 * Props:
 *   settings        — current settings object (spread of BASE_GAME_DEFAULTS)
 *   onSettingChange — (key, value) => void
 *   disabled        — true when the viewer is not the host (read-only display)
 *   showPitchMod    — show the Pitch Mod selector (default false; enabled for bot/pvp modes)
 *   rarityMode      — "editable"     : interactive checkboxes (default)
 *                     "draft-locked" : read-only echo pointing at the Deck
 *                                      Draft section (deck-builder mode)
 *   weather         — explicit weather object override (used by BotVsBotSetup before game exists)
 *   onRerollWeather — () => void | null
 *   game            — full game object (weather falls back to game?.weather when weather prop is null)
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { ChevronDown, RefreshCw } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TIMER_META, TIMER_KEYS } from "@/constants/timers";
import { RARITY_OPTIONS } from "@/constants/rarities";
import { INNINGS_OPTIONS } from "@/constants/game";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Internal primitives ────────────────────────────────────────────────────── */
function SectionLabel({ children }) {
  return <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{children}</p>;
}

function CollapseToggle({ open, onToggle, label }) {
  return (
    <button type="button" onClick={onToggle}
      className="flex items-center gap-1.5 text-[10px] font-black text-slate-600 hover:text-slate-400 uppercase tracking-widest transition-colors w-full">
      <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      {label}
      <span className="flex-1 h-px bg-slate-800 ml-1" />
    </button>
  );
}

export function Toggle({ value, onChange, disabled = false }) {
  return (
    <button onClick={() => !disabled && onChange(!value)} disabled={disabled}
      className={`relative overflow-hidden w-10 h-5 rounded-full transition-colors flex-shrink-0
        ${value ? "bg-amber-500" : "bg-slate-700"}
        ${disabled ? "cursor-not-allowed opacity-60" : ""}`}>
      <span className={`absolute left-0 top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform
        ${value ? "translate-x-[22px]" : "translate-x-0.5"}`} />
    </button>
  );
}

/* ─── Main component ─────────────────────────────────────────────────────────── */
export default function GameSettingsPanel({
  settings = {},
  onSettingChange,
  disabled = false,
  showPitchMod = false,
  rarityMode = "editable",
  weather = null,
  onRerollWeather = null,
  game = null,
  // `draftsSupported = false` greys out the Deck Builder Draft + Player
  // Draft toggles and adds a "Not supported in this mode" caption. Bot
  // and bot-vs-bot lobbies pass this because the draft flows (snake
  // packs, ready-up handshake, per-side WebSocket dispatch) don't have
  // bot-side implementations yet — flipping them on in those modes would
  // softlock the lobby waiting on a bot pick that never comes.
  draftsSupported = true,
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [rarityCounts, setRarityCounts] = useState({});

  useEffect(() => {
    if (rarityMode === "editable") {
      axios.get(`${API}/cards/rarity-counts`).then(r => setRarityCounts(r.data)).catch(() => {});
    }
  }, [rarityMode]);

  const effectiveWeather = weather ?? game?.weather ?? null;

  const {
    innings_total = 9,
    ballpark = "",
    draft_enabled = false,
    excluded_rarities = ["junk_wax"],
    chip_max = 4,
    pitch_mod = 2,
    hand_size = 5,
    fast_mode = false,
    no_timers = false,
    require_chip_match_for_hr = true,
    depleting_chips = false,
    chip_arsenal = false,
    chip_arsenal_infinite = 1,
    chip_arsenal_modifiers = [1, 1, 1, 1],
    chip_arsenal_fb_count = 8,
    chip_arsenal_ch_count = 5,
    chip_arsenal_cb_count = 3,
    chip_arsenal_sl_count = 2,
    continuous_draw = false,
    heat_map = false,
    heat_map_overlap_bonus = 1,
    heat_map_chip_bonus = 1,
    heat_map_overhang_threshold = 20,
    heat_map_zone_width_pct = 100,
    same_raw_tie_is_ball = false,
    modifier_tie_is_ball = false,
    any_raw_tie_is_ball = true,
    called_ball_tie_is_strike = true,
    bluff_values = true,
    unlimited_stacking = true,
    player_draft_enabled = false,
  } = settings;

  const timerVal = (key) => {
    const v = settings[key];
    return v != null && v > 0 ? String(v) : "";
  };
  const handleTimerInput = (key, raw) => {
    const n = parseInt(raw, 10);
    onSettingChange(key, !raw || isNaN(n) || n <= 0 ? null : n);
  };

  const arsenalBlockedByBluff = bluff_values && !chip_arsenal;
  const bluffBlockedByArsenal = chip_arsenal && !bluff_values;
  const pitchModOverride = chip_arsenal ? "Pitch Arsenal"
    : heat_map ? "Heat Map"
    : bluff_values ? "Got Your Number"
    : null;

  return (
    <div className={`rounded-2xl border border-slate-800 bg-slate-900/60 p-5 space-y-4 mb-6 ${disabled ? "opacity-70" : ""}`}>
      <div className="flex items-center justify-between">
        <SectionLabel>Game Settings</SectionLabel>
        {disabled && <span className="text-[10px] text-slate-600 font-medium">host controls</span>}
      </div>

      {/* Innings · Ballpark · Weather */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest">Innings</label>
          {disabled ? (
            <p className="text-sm text-white font-bold py-2">{innings_total} innings</p>
          ) : (
            <Select value={String(innings_total)} onValueChange={v => onSettingChange("innings_total", parseInt(v))}>
              <SelectTrigger className="w-full px-3 py-2.5 h-auto rounded-xl bg-slate-800/80 border border-slate-700 text-white text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {INNINGS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="space-y-1.5">
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest">
            Ballpark <span className="text-slate-700 normal-case font-medium">optional</span>
          </label>
          {disabled ? (
            <p className="text-sm text-white font-bold py-2">{ballpark || "Mutual Distrust Park"}</p>
          ) : (
            <input placeholder="e.g. Mutual Distrust Park" value={ballpark}
              onChange={e => onSettingChange("ballpark", e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl bg-slate-800/80 border border-slate-700 text-white placeholder-slate-600 text-sm outline-none focus:ring-2 focus:ring-amber-500/40 transition-all" />
          )}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest">Weather</label>
            {!disabled && onRerollWeather && (
              <button onClick={onRerollWeather} title="Re-roll weather"
                className="text-slate-500 hover:text-amber-400 transition-colors p-0.5 rounded">
                <RefreshCw size={11} />
              </button>
            )}
          </div>
          {effectiveWeather ? (
            <div className="flex items-center gap-2 py-1">
              <span className="text-base leading-none">{effectiveWeather.emoji}</span>
              <span className="text-sm text-white font-bold">{effectiveWeather.temp}° {effectiveWeather.condition}</span>
            </div>
          ) : (
            <p className="text-sm text-slate-600 italic py-2">—</p>
          )}
        </div>
      </div>

      <CollapseToggle open={advancedOpen} onToggle={() => setAdvancedOpen(o => !o)} label="Enhanced Mechanics" />

      {advancedOpen && (
        <div className="space-y-4 pt-1">

          {/* Deck Builder Draft + Player Draft live at the top of Enhanced
              Mechanics so the two draft modes sit together as the headline
              opt-ins. Both are server-locked once their draft has started
              (mid-draft toggle would soft-lock the lobby). */}
          {(() => {
            const draftLocked = game?.draft_phase && game.draft_phase !== "idle";
            const toggleDisabled = disabled || draftLocked || !draftsSupported;
            return (
              <div className={`flex items-center justify-between p-3 rounded-xl border ${
                draft_enabled
                  ? "bg-amber-500/10 border-amber-500/40"
                  : "bg-slate-800/40 border-slate-700/60"
              } ${toggleDisabled ? "opacity-80" : ""}`}>
                <div className="min-w-0 pr-3">
                  <p className={`text-xs font-black tracking-wider ${draft_enabled ? "text-amber-300" : "text-slate-200"}`}>
                    Deck Builder Draft
                  </p>
                  <p className="text-[10px] text-slate-500 leading-snug mt-0.5">
                    {draft_enabled
                      ? "Both players draft a shared manager-card deck before rosters."
                      : "Toggle on to draft manager card deck(s)."}
                    {draftLocked && (
                      <span className="block text-amber-500/80 italic mt-0.5">Locked — draft in progress.</span>
                    )}
                    {!draftsSupported && (
                      <span className="block text-slate-600 italic mt-0.5">Not yet supported in this mode.</span>
                    )}
                  </p>
                </div>
                {disabled ? (
                  <span className={`text-xs font-bold ${draft_enabled ? "text-amber-400" : "text-slate-600"}`}>
                    {draft_enabled ? "On" : "Off"}
                  </span>
                ) : (
                  <Toggle
                    value={draft_enabled}
                    onChange={(v) => !toggleDisabled && onSettingChange("draft_enabled", v)}
                    disabled={toggleDisabled}
                  />
                )}
              </div>
            );
          })()}

          {(() => {
            const pdLocked = game?.player_draft_phase && game.player_draft_phase !== "idle";
            const toggleDisabled = disabled || pdLocked || !draftsSupported;
            return (
              <div className={`flex items-center justify-between p-3 rounded-xl border ${
                player_draft_enabled
                  ? "bg-amber-500/10 border-amber-500/40"
                  : "bg-slate-800/40 border-slate-700/60"
              } ${toggleDisabled ? "opacity-80" : ""}`}>
                <div className="min-w-0 pr-3">
                  <p className={`text-xs font-black tracking-wider ${player_draft_enabled ? "text-amber-300" : "text-slate-200"}`}>
                    Player Draft
                  </p>
                  <p className="text-[10px] text-slate-500 leading-snug mt-0.5">
                    {player_draft_enabled
                      ? "Both players draft their rosters from a shared player pool (snake order, 6-card packs)."
                      : "Toggle on to draft player rosters instead of randomizing."}
                    {pdLocked && (
                      <span className="block text-amber-500/80 italic mt-0.5">Locked — draft in progress.</span>
                    )}
                    {!draftsSupported && (
                      <span className="block text-slate-600 italic mt-0.5">Not yet supported in this mode.</span>
                    )}
                  </p>
                </div>
                {disabled ? (
                  <span className={`text-xs font-bold ${player_draft_enabled ? "text-amber-400" : "text-slate-600"}`}>
                    {player_draft_enabled ? "On" : "Off"}
                  </span>
                ) : (
                  <Toggle
                    value={player_draft_enabled}
                    onChange={(v) => !toggleDisabled && onSettingChange("player_draft_enabled", v)}
                    disabled={toggleDisabled}
                  />
                )}
              </div>
            );
          })()}

          {/* Manager Card Deck */}
          <div className="space-y-2">
            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest">Manager Card Deck</label>
            {rarityMode === "draft-locked" ? (
              <>
                <div className="grid grid-cols-2 gap-1.5 opacity-50">
                  {RARITY_OPTIONS.map(r => {
                    const active = !excluded_rarities.includes(r.key);
                    return (
                      <div key={r.key} title="Set in the Deck Draft section below"
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium cursor-not-allowed select-none
                          ${active ? "bg-slate-800/60 border-slate-700/60" : "bg-slate-900/40 border-slate-800"}`}>
                        <div className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center flex-shrink-0
                          ${active ? "bg-amber-500/60 border-amber-500/60" : "border-slate-700"}`}>
                          {active && <svg viewBox="0 0 10 8" className="w-2.5 h-2.5"><path d="M1 4l3 3 5-6" stroke="black" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                        </div>
                        <span className={active ? r.color : "text-slate-600"}>{r.label}</span>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-slate-600 italic">Set in the Deck Draft section below.</p>
              </>
            ) : disabled ? (
              <div className="flex flex-wrap gap-x-3 gap-y-1 py-1">
                {RARITY_OPTIONS.map(r => {
                  const active = !excluded_rarities.includes(r.key);
                  const count = rarityCounts[r.key];
                  return (
                    <span key={r.key} className={`text-[11px] font-medium ${active ? r.color : "text-slate-700 line-through"}`}>
                      {r.label}{count != null && <span className="text-[10px] text-slate-600"> ({count})</span>}
                    </span>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-1.5">
                {RARITY_OPTIONS.map(r => {
                  const active = !excluded_rarities.includes(r.key);
                  const count = rarityCounts[r.key];
                  return (
                    <button key={r.key}
                      onClick={() => onSettingChange("excluded_rarities", active
                        ? [...excluded_rarities, r.key]
                        : excluded_rarities.filter(x => x !== r.key))}
                      className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-colors text-sm font-medium
                        ${active ? "bg-slate-800 border-slate-600 text-white" : "bg-slate-900/40 border-slate-800 text-slate-600"}`}>
                      <div className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center flex-shrink-0 transition-colors
                        ${active ? "bg-amber-500 border-amber-500" : "border-slate-700"}`}>
                        {active && <svg viewBox="0 0 10 8" className="w-2.5 h-2.5"><path d="M1 4l3 3 5-6" stroke="black" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                      </div>
                      <div className="flex-1 text-left">
                        <span className={r.color}>{r.label}</span>
                        {count != null && <span className="text-[10px] text-slate-600 ml-1">({count})</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Pitch Chips + optional Pitch Mod */}
          <div className={`grid gap-4 ${showPitchMod ? "sm:grid-cols-2" : "grid-cols-1"}`}>
            <div className="space-y-2">
              <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                Pitch Chips <span className="normal-case font-medium text-slate-700">highest chip a pitcher can throw</span>
              </label>
              {disabled ? (
                <p className="text-sm text-white font-bold py-1">{chip_max} chips</p>
              ) : (
                <div className="grid grid-cols-4 gap-1.5">
                  {[1, 2, 3, 4].map(n => (
                    <button key={n} onClick={() => onSettingChange("chip_max", n)}
                      className={`py-2.5 rounded-xl text-sm font-black border transition-colors
                        ${chip_max === n ? "bg-amber-500 border-amber-500 text-black" : "bg-slate-800/80 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                      {n}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {showPitchMod && (
              <div className={`space-y-2 ${pitchModOverride ? "opacity-40" : ""}`}>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                  Pitch Mod{" "}
                  <span className="normal-case font-medium text-slate-700">
                    {pitchModOverride ? `overridden by ${pitchModOverride}` : "bonus on chip match"}
                  </span>
                </label>
                {disabled ? (
                  <p className="text-sm text-white font-bold py-1">{pitch_mod}</p>
                ) : (
                  <div className="flex gap-1.5">
                    {[1, 2, 3].map(n => (
                      <button key={n} disabled={!!pitchModOverride}
                        onClick={() => { if (!pitchModOverride) onSettingChange("pitch_mod", n); }}
                        className={`flex-1 py-2.5 rounded-xl text-sm font-black border transition-colors
                          ${pitch_mod === n
                            ? (pitchModOverride ? "bg-amber-500/60 border-amber-500/60 text-black" : "bg-amber-500 border-amber-500 text-black")
                            : "bg-slate-800/80 border-slate-700 text-slate-400 hover:border-slate-500"}
                          ${pitchModOverride ? "cursor-not-allowed" : ""}`}>
                        {n}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Hand Size */}
          <div className="space-y-2">
            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest">
              Hand Size <span className="normal-case font-medium text-slate-700">manager cards each player holds</span>
            </label>
            {disabled ? (
              <p className="text-sm text-white font-bold py-1">{hand_size} cards</p>
            ) : (
              <div className="flex gap-1.5">
                {[0, 1, 2, 3, 4, 5, 6].map(n => (
                  <button key={n} onClick={() => onSettingChange("hand_size", n)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-black border transition-colors
                      ${hand_size === n ? "bg-amber-500 border-amber-500 text-black" : "bg-slate-800/80 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                    {n}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Simple boolean toggles */}
          {[
            { key: "fast_mode",               val: fast_mode,               label: "Fast Mode",                  desc: "Cuts all timers to ~10s" },
            { key: "no_timers",               val: no_timers,               label: "No Timers",                  desc: "Removes all timers — players must act manually; 10-min AFK safety net applies" },
            { key: "require_chip_match_for_hr",val: require_chip_match_for_hr,label: "Chip Match Required for HR",desc: "Batters must guess the pitch type to hit a home run — mismatched contact caps at 6 and forces a fielding play" },
            { key: "unlimited_stacking",      val: unlimited_stacking,      label: "Unlimited Stacking",         desc: "Removes the max-modifier cap on manager cards and pitch chips — modifiers stack with no upper limit" },
            { key: "depleting_chips",         val: depleting_chips,         label: "Depleting Chips",            desc: "A correct pitch chip guess by the batter locks that chip for the rest of the at-bat — the pitcher's last remaining chip is always safe" },
            { key: "continuous_draw",         val: continuous_draw,         label: "Continuous Draw",            desc: "Played cards are replaced immediately instead of waiting on the half-inning trade-back" },
          ].map(({ key, val, label, desc }) => (
            <div key={key} className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-slate-400">{label}</p>
                <p className="text-[10px] text-slate-600">{desc}</p>
              </div>
              {disabled
                ? <span className={`text-xs font-bold ${val ? "text-amber-400" : "text-slate-600"}`}>{val ? "On" : "Off"}</span>
                : <Toggle value={val} onChange={v => onSettingChange(key, v)} />}
            </div>
          ))}

          {/* Pitch Arsenal */}
          <div className={`flex items-center justify-between ${arsenalBlockedByBluff ? "opacity-40" : ""}`}>
            <div>
              <p className="text-xs font-bold text-slate-400">Pitch Arsenal</p>
              <p className="text-[10px] text-slate-600">Each pitch type has a limited per-inning supply and its own bonus — except the infinite chip, which never runs out</p>
              {arsenalBlockedByBluff && !disabled && <p className="text-[10px] text-amber-500/80 italic mt-0.5">Turn off Got Your Number to enable</p>}
            </div>
            {disabled
              ? <span className={`text-xs font-bold ${chip_arsenal ? "text-amber-400" : "text-slate-600"}`}>{chip_arsenal ? "On" : "Off"}</span>
              : <Toggle value={chip_arsenal} onChange={v => { if (!arsenalBlockedByBluff) onSettingChange("chip_arsenal", v); }} />}
          </div>

          {chip_arsenal && (
            <>
              <div className="flex items-center justify-between pl-4 border-l-2 border-amber-500/30">
                <div>
                  <p className="text-xs font-bold text-slate-400">Infinite Chip</p>
                  <p className="text-[10px] text-slate-600">The chip type that never depletes — every pitcher always has this one available</p>
                </div>
                <div className="flex gap-1">
                  {[{n:1,abbr:"FB"},{n:2,abbr:"CH"},{n:3,abbr:"CB"},{n:4,abbr:"SL"}].map(({n, abbr}) => (
                    <button key={n} onClick={() => !disabled && onSettingChange("chip_arsenal_infinite", n)}
                      disabled={disabled}
                      className={`w-9 h-7 rounded text-[10px] font-bold border
                        ${chip_arsenal_infinite === n ? "bg-amber-500 border-amber-500 text-black" : "bg-slate-800/80 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                      {abbr}∞
                    </button>
                  ))}
                </div>
              </div>

              {[{n:1,abbr:"FB"},{n:2,abbr:"CH"},{n:3,abbr:"CB"},{n:4,abbr:"SL"}].map(({n, abbr}) => (
                <div key={n} className="flex items-center justify-between pl-4 border-l-2 border-amber-500/30">
                  <div>
                    <p className="text-xs font-bold text-slate-400">{abbr} Bonus {chip_arsenal_infinite === n ? <span className="text-amber-400 text-[9px]">∞</span> : null}</p>
                    <p className="text-[10px] text-slate-600">Match bonus when pitcher throws {abbr}</p>
                  </div>
                  <div className="flex gap-1">
                    {[0,1,2,3,4].map(v => (
                      <button key={v} disabled={disabled}
                        onClick={() => {
                          if (disabled) return;
                          const next = [...chip_arsenal_modifiers]; next[n - 1] = v;
                          onSettingChange("chip_arsenal_modifiers", next);
                        }}
                        className={`w-7 h-7 rounded text-xs font-bold border
                          ${chip_arsenal_modifiers[n-1] === v ? "bg-amber-500 border-amber-500 text-black" : "bg-slate-800/80 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              ))}

              <div className="pl-4 border-l-2 border-amber-500/30 space-y-2">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Per-Pitcher Supply</p>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { n: 1, abbr: "FB", countKey: "chip_arsenal_fb_count", val: chip_arsenal_fb_count },
                    { n: 2, abbr: "CH", countKey: "chip_arsenal_ch_count", val: chip_arsenal_ch_count },
                    { n: 3, abbr: "CB", countKey: "chip_arsenal_cb_count", val: chip_arsenal_cb_count },
                    { n: 4, abbr: "SL", countKey: "chip_arsenal_sl_count", val: chip_arsenal_sl_count },
                  ].map(({ n, abbr, countKey, val }) => {
                    const isInfinite = chip_arsenal_infinite === n;
                    return (
                      <div key={n} className="space-y-1">
                        <p className={`text-[10px] font-bold text-center uppercase ${isInfinite ? "text-amber-400" : "text-slate-500"}`}>
                          {abbr}{isInfinite ? "∞" : ""}
                        </p>
                        {isInfinite ? (
                          <p className="text-[10px] text-center text-amber-400/60 font-bold">∞</p>
                        ) : disabled ? (
                          <p className="text-xs font-bold text-center text-slate-400">{val}</p>
                        ) : (
                          <input type="number" min={1} max={99} value={val}
                            onChange={e => {
                              const parsed = parseInt(e.target.value, 10);
                              if (!isNaN(parsed) && parsed >= 1) onSettingChange(countKey, parsed);
                            }}
                            className="w-full px-1 py-1 rounded text-xs font-bold text-center bg-slate-800/80 border border-slate-700 text-white focus:outline-none focus:border-amber-500" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* Got Your Number */}
          <div className={`flex items-center justify-between ${bluffBlockedByArsenal ? "opacity-40" : ""}`}>
            <div>
              <p className="text-xs font-bold text-slate-400">Got Your Number</p>
              <p className="text-[10px] text-slate-600">Each pitcher secretly assigns 1, 1, 2, 3 across their four chips. On a chip match the batter wins the higher of the two values; on a mismatch the pitcher wins.</p>
              {bluffBlockedByArsenal && !disabled && <p className="text-[10px] text-amber-500/80 italic mt-0.5">Turn off Pitch Arsenal to enable</p>}
            </div>
            {disabled
              ? <span className={`text-xs font-bold ${bluff_values ? "text-amber-400" : "text-slate-600"}`}>{bluff_values ? "On" : "Off"}</span>
              : <Toggle value={bluff_values} onChange={v => { if (!bluffBlockedByArsenal) onSettingChange("bluff_values", v); }} />}
          </div>

          {/* Heat Map Pitch Mode */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-slate-400">Heat Map Pitch Mode</p>
              <p className="text-[10px] text-slate-600">Pitcher and batter drag chips onto a strike zone. Overlapping circles and matching pitch types each award their own bonus, scored independently</p>
            </div>
            {disabled
              ? <span className={`text-xs font-bold ${heat_map ? "text-amber-400" : "text-slate-600"}`}>{heat_map ? "On" : "Off"}</span>
              : <Toggle value={heat_map} onChange={v => onSettingChange("heat_map", v)} />}
          </div>

          {heat_map && (
            <div className="space-y-3 pl-4 border-l-2 border-amber-500/30">
              {[
                { key: "heat_map_overlap_bonus", val: heat_map_overlap_bonus, label: "Overlap Bonus",          desc: "Awarded to the batter when their chip circle intersects the pitcher's" },
                { key: "heat_map_chip_bonus",    val: heat_map_chip_bonus,    label: "Pitch Type Guess Bonus", desc: "Awarded to the batter when their chip type matches the pitcher's" },
              ].map(({ key, val, label, desc }) => (
                <div key={key} className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold text-slate-400">{label}</p>
                    <p className="text-[10px] text-slate-600">{desc}</p>
                  </div>
                  {disabled ? <span className="text-xs font-bold text-amber-400">{val}</span> : (
                    <div className="flex gap-1">
                      {[0, 1, 2, 3].map(n => (
                        <button key={n} onClick={() => onSettingChange(key, n)}
                          className={`w-7 h-7 rounded text-xs font-bold border transition-colors
                            ${val === n ? "bg-amber-500 border-amber-500 text-black" : "bg-slate-800/80 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                          {n}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-400">Overhang Threshold (%)</p>
                  <p className="text-[10px] text-slate-600">How far a chip can hang off the strike zone edge and still place — as a percentage of its radius. Higher = more forgiving</p>
                </div>
                {disabled ? <span className="text-xs font-bold text-amber-400">{heat_map_overhang_threshold}%</span> : (
                  <input type="number" min={0} max={50} value={heat_map_overhang_threshold ?? 20}
                    onChange={e => { const n = parseInt(e.target.value, 10); if (!isNaN(n) && n >= 0 && n <= 50) onSettingChange("heat_map_overhang_threshold", n); }}
                    className="w-16 px-2 py-1 rounded text-xs font-bold text-center bg-slate-800/80 border border-slate-700 text-white focus:outline-none focus:border-amber-500" />
                )}
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-slate-400">Zone Width (%)</p>
                  <p className="text-[10px] text-slate-600">Width of the strike zone relative to default. Smaller zones make overlapping harder for both sides</p>
                </div>
                {disabled ? <span className="text-xs font-bold text-amber-400">{heat_map_zone_width_pct}%</span> : (
                  <div className="flex gap-1">
                    {[60, 70, 80, 90, 100].map(pct => (
                      <button key={pct} onClick={() => onSettingChange("heat_map_zone_width_pct", pct)}
                        className={`px-2 py-1 rounded text-xs font-bold border transition-colors
                          ${heat_map_zone_width_pct === pct ? "bg-amber-500 border-amber-500 text-black" : "bg-slate-800/80 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                        {pct}%
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tie Rules. `on`/`off` name each row's two outcomes for the read-only
              (non-host) display; `muted` dims a row whose rule a broader toggle
              has taken over — still clickable, since it governs again the moment
              the override goes back off. */}
          {[
            { key: "same_raw_tie_is_ball", value: same_raw_tie_is_ball, label: "Raw Die Tie → Ball", on: "Ball", off: "Foul",
              desc: "ON: matching raw d10s = ball  |  OFF: matching raw d10s = foul  (excludes 0/0 wild pitch, 1/1 HBP)",
              muted: any_raw_tie_is_ball },
            { key: "any_raw_tie_is_ball", value: any_raw_tie_is_ball, label: "Any Raw Tie → Ball", on: "Ball", off: "Normal",
              desc: "ON: every matching raw d10 pair = ball, 0/0 and 1/1 included — no wild pitch, no HBP, and Raw Die Tie → Ball stops mattering  |  OFF: normal rules  (All or Nothing and Derby Swing still win the tie either way)" },
            { key: "modifier_tie_is_ball",  value: modifier_tie_is_ball,  label: "Modifier Tie → Ball", on: "Ball", off: "Foul",
              desc: "ON: modifier-equalized totals = ball  |  OFF: modifier-equalized totals = foul — switch OFF for the any-modified-tie-is-a-foul ruleset" },
            { key: "called_ball_tie_is_strike", value: called_ball_tie_is_strike, label: "Take Pitch Tie → Strike", on: "Strike", off: "Ball",
              desc: "ON: a tied take-pitch rolloff is a strike (pitcher wins ties)  |  OFF: a tied rolloff is a ball (batter wins ties)" },
          ].map(({ key, value, label, desc, on, off, muted }) => (
            <div key={key} className={`flex items-center justify-between ${muted ? "opacity-40" : ""}`}>
              <div>
                <p className="text-xs font-bold text-slate-400">{label}</p>
                <p className="text-[10px] text-slate-600">{muted ? "overridden by Any Raw Tie → Ball" : desc}</p>
              </div>
              {disabled
                ? <span className={`text-xs font-bold ${value ? "text-amber-400" : "text-slate-600"}`}>{value ? on : off}</span>
                : <Toggle value={value} onChange={v => onSettingChange(key, v)} />}
            </div>
          ))}

          {/* Custom Clocks — the entire block goes inactive under No Timers since
              the values aren't honored (server uses the AFK safety net instead). */}
          {(() => {
            const timersInactive = no_timers;
            const visibleKeys = TIMER_KEYS.filter(k => {
              const gate = TIMER_META[k].draftOnly;
              if (!gate) return true;
              return !!settings[gate];
            });
            return (
              <div className={`space-y-2.5 ${timersInactive ? "opacity-40 pointer-events-none" : ""}`}>
                <div className="flex items-baseline justify-between">
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest">Custom Clocks</label>
                  <span className="text-[10px] text-slate-700">
                    {timersInactive ? "disabled — No Timers is on" : "empty = use default"}
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {visibleKeys.map(key => (
                    <div key={key} className="space-y-1">
                      <label className="block text-[10px] font-bold text-slate-600 uppercase tracking-widest">{TIMER_META[key].label}</label>
                      {disabled || timersInactive ? (
                        <p className="text-sm text-slate-400 py-1">
                          {settings[key] > 0 ? `${settings[key]}s` : `${TIMER_META[key].default}s`}
                        </p>
                      ) : (
                        <div className="relative">
                          <input type="number" min="5" max={String(TIMER_META[key].max)}
                            placeholder={String(TIMER_META[key].default)}
                            value={timerVal(key)}
                            onChange={e => handleTimerInput(key, e.target.value)}
                            className="w-full px-3 py-2 rounded-lg bg-slate-800/80 border border-slate-700 text-white placeholder-slate-600 text-sm outline-none focus:ring-2 focus:ring-amber-500/40 transition-all pr-6 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" />
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-600 pointer-events-none">s</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

        </div>
      )}
    </div>
  );
}
